import { useEffect, useRef, useState } from "react";
import type { RoundName } from "../../domain/round/types";
import { groupScheduleStatus } from "./resilientSchedule";
import type { DpWorldGroup } from "./dpworldApi";
import { loadGeneratorDraft, saveGeneratorDraft, type GeneratorGroupOverride } from "./generatorDraftApi";
import "./tee-times-review.css";

type TeeGroup = Record<string, unknown>;
type LoadedOverrides = { key: string; rows: GeneratorGroupOverride[] };

function groupNumber(group: TeeGroup, index: number) {
  return Number(group.group ?? group.groupNumber ?? index + 1) || index + 1;
}

function groupTime(group: TeeGroup) {
  const status = groupScheduleStatus(group as DpWorldGroup);
  if (status === "postponed") return "PRZEŁOŻONA";
  if (status === "pending") return "—";
  if (status === "suspended") return "PRZERWANA";
  const value = String(group.start ?? group.startTime ?? group.time ?? group.teeTime ?? "").trim();
  return value && value !== "00:00" ? value : "—";
}

function groupTee(group: TeeGroup) {
  return String(group.tee ?? group.startTee ?? "—");
}

function playerNames(group: TeeGroup) {
  const players = Array.isArray(group.players) ? group.players : [];
  return players.map((player) => {
    if (typeof player === "string") return player;
    if (player && typeof player === "object") {
      const row = player as Record<string, unknown>;
      return String(row.name ?? row.playerName ?? row.displayName ?? "").trim();
    }
    return "";
  }).filter(Boolean).join(" · ");
}

function coverage(group: TeeGroup) {
  const explicit = Number(group.requiredScorers ?? group.coverageScorers ?? 0);
  if (explicit > 0) return Math.min(3, Math.max(1, explicit));
  return group.tv ? 2 : 1;
}

function overridesFromGroups(groups: TeeGroup[]): GeneratorGroupOverride[] {
  return groups.map((group, index) => {
    const scorers = coverage(group);
    return {
      group: groupNumber(group, index),
      tv: Boolean(group.tv),
      requiredScorers: scorers,
      coverageScorers: scorers,
      teePin: Boolean(group.teePin),
    };
  });
}

function overrideMatches(group: TeeGroup, saved: GeneratorGroupOverride) {
  const savedCoverage = Math.min(3, Math.max(1, Number(saved.requiredScorers || saved.coverageScorers || 1)));
  return Boolean(group.tv) === Boolean(saved.tv)
    && coverage(group) === savedCoverage
    && Boolean(group.teePin) === Boolean(saved.teePin);
}

function mergeOverrides(groups: TeeGroup[], overrides: GeneratorGroupOverride[]) {
  const byGroup = new Map(overrides.map((row) => [Number(row.group), row]));
  let changed = false;
  const merged = groups.map((group, index) => {
    const saved = byGroup.get(groupNumber(group, index));
    if (!saved || overrideMatches(group, saved)) return group;
    changed = true;
    return { ...group, ...saved };
  });
  return { groups: merged, changed };
}

export function TeeTimesReviewPanel({
  tournamentId,
  round,
  groups,
  onChange,
  teePinEnabled,
}: {
  tournamentId?: string;
  round: RoundName;
  groups: TeeGroup[];
  onChange: (groups: TeeGroup[]) => void;
  teePinEnabled: boolean;
}) {
  const saveTimer = useRef<number | null>(null);
  const pending = useRef<TeeGroup[] | null>(null);
  const [loaded, setLoaded] = useState<LoadedOverrides>({ key: "", rows: [] });
  const [saveState, setSaveState] = useState<"idle" | "loading" | "saving" | "saved" | "error">("idle");
  const key = `${tournamentId || ""}:${round}`;

  function flush() {
    if (!tournamentId || !pending.current) return;
    const rows = pending.current;
    pending.current = null;
    if (saveTimer.current != null) {
      window.clearTimeout(saveTimer.current);
      saveTimer.current = null;
    }
    void saveGeneratorDraft(tournamentId, round, overridesFromGroups(rows))
      .then((draft) => {
        setLoaded({ key, rows: draft.overrides || [] });
        setSaveState("saved");
      })
      .catch(() => setSaveState("error"));
  }

  useEffect(() => {
    if (!tournamentId) return;
    let cancelled = false;
    setLoaded({ key: "", rows: [] });
    setSaveState("loading");
    void loadGeneratorDraft(tournamentId, round)
      .then((draft) => {
        if (cancelled) return;
        setLoaded({ key, rows: draft.overrides || [] });
        setSaveState("saved");
      })
      .catch(() => { if (!cancelled) setSaveState("error"); });
    return () => {
      cancelled = true;
      flush();
    };
  }, [tournamentId, round]);

  useEffect(() => {
    if (!groups.length || loaded.key !== key || !loaded.rows.length) return;
    const merged = mergeOverrides(groups, loaded.rows);
    if (merged.changed) onChange(merged.groups);
  }, [groups, loaded, key, onChange]);

  useEffect(() => () => flush(), []);

  function persist(nextGroups: TeeGroup[]) {
    if (!tournamentId) return;
    pending.current = nextGroups;
    setLoaded({ key, rows: overridesFromGroups(nextGroups) });
    if (saveTimer.current != null) window.clearTimeout(saveTimer.current);
    setSaveState("saving");
    saveTimer.current = window.setTimeout(flush, 250);
  }

  if (!groups.length) return null;

  const tvCount = groups.filter((group) => Boolean(group.tv)).length;
  const extraCoverage = groups.reduce((total, group) => total + Math.max(0, coverage(group) - 1), 0);
  const pendingCount = groups.filter((group) => ["pending", "postponed"].includes(groupScheduleStatus(group as DpWorldGroup))).length;

  function patch(index: number, values: TeeGroup) {
    const next = groups.map((group, current) => current === index ? { ...group, ...values } : group);
    onChange(next);
    persist(next);
  }

  function toggleTv(index: number, checked: boolean) {
    const group = groups[index];
    const currentCoverage = coverage(group);
    patch(index, {
      tv: checked,
      requiredScorers: checked ? Math.max(2, currentCoverage) : 1,
      coverageScorers: checked ? Math.max(2, currentCoverage) : 1,
    });
  }

  function setCoverage(index: number, value: number) {
    const next = Math.min(3, Math.max(1, value));
    patch(index, { requiredScorers: next, coverageScorers: next, tv: next > 1 ? Boolean(groups[index].tv) : false });
  }

  return (
    <section className="tee-review" aria-label={`${round} TV i obsada grup`}>
      <header className="tee-review-head">
        <div>
          <span>TV / DODATKOWA OBSADA · {round}</span>
          <strong>{groups.length} grup</strong>
        </div>
        <div className="tee-review-summary">
          <span><b>{tvCount}</b> TV</span>
          <span><b>{extraCoverage}</b> dodatkowych scorerów</span>
          {pendingCount ? <span><b>{pendingCount}</b> bez oficjalnej godziny</span> : null}
          {saveState === "saving" || saveState === "loading" ? <span className="tee-review-sync">● zapisuję…</span> : null}
          {saveState === "saved" ? <span className="tee-review-sync ok">✓ zapisane w PostgreSQL</span> : null}
          {saveState === "error" ? <span className="tee-review-sync error">! błąd zapisu</span> : null}
        </div>
      </header>

      <p className="tee-review-help">TV, liczba scorerów i Tee/Pin są zapisane dla tego turnieju i rundy. Odświeżenie strony oraz ponowne pobranie Tee Times z DPWT zachowują ustawienia.</p>
      {pendingCount ? <p className="generator-notice">Grupy bez godziny pozostają w rundzie. Generator może przygotować scorerów awaryjnie, ale transport jest wyłączony do czasu wczytania prawdziwych tee times.</p> : null}

      <div className="tee-review-table-wrap">
        <table className="tee-review-table">
          <thead><tr><th>Grupa</th><th>Start</th><th>Tee</th><th>Zawodnicy</th><th>TV</th><th>Scorerzy</th>{teePinEnabled ? <th>Tee/Pin</th> : null}</tr></thead>
          <tbody>
            {groups.map((group, index) => {
              const number = groupNumber(group, index);
              const status = groupScheduleStatus(group as DpWorldGroup);
              return (
                <tr key={`${round}-${number}-${index}`} className={group.tv ? "is-tv" : ""}>
                  <td><strong>{number}</strong></td>
                  <td><strong>{groupTime(group)}</strong>{status === "postponed" ? <small> · 00:00 z DPWT</small> : null}</td>
                  <td>{groupTee(group)}</td>
                  <td className="tee-review-players">{playerNames(group) || "—"}</td>
                  <td><label className="tee-review-check"><input type="checkbox" checked={Boolean(group.tv)} onChange={(event) => toggleTv(index, event.target.checked)} /><span>TV</span></label></td>
                  <td><select aria-label={`Liczba scorerów grupa ${number}`} value={coverage(group)} onChange={(event) => setCoverage(index, Number(event.target.value))}><option value={1}>1</option><option value={2}>2</option><option value={3}>3</option></select></td>
                  {teePinEnabled ? <td><label className="tee-review-check"><input type="checkbox" checked={Boolean(group.teePin)} onChange={(event) => patch(index, { teePin: event.target.checked })} /><span>T/P</span></label></td> : null}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
